// Pausa do agente: os motivos cadastrados na conta e a pausa ABERTA do usuário
// logado. Pausar = ficar Ocupado com um motivo — o PUT de disponibilidade faz o
// resto no servidor (Relatório de Pausas e, pra quem tem ramal, as filas de
// telefonia). O estado aqui é o que a barra superior, o menu de status e o
// discador leem, então os três mostram a mesma pausa.
import types from '../mutation-types';
import PauseReasonsAPI from '../../api/pauseReasons';
import MyPauseAPI from '../../api/myPause';
import { throwErrorMessage } from '../utils/api';

const byName = (a, b) => a.name.localeCompare(b.name);

const EMPTY_PAUSE = { reason: '', startedAt: null };

export const state = {
  reasons: [],
  // startedAt em ms. Vem do servidor (sobrevive a F5 e à pausa feita em outra
  // aba) ou do instante local logo depois de pausar, enquanto o job ainda não
  // gravou o período.
  current: { ...EMPTY_PAUSE },
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isChangingPause: false,
    // Diálogo "motivo da pausa" (PauseReasonDialog, montado na barra superior).
    isReasonDialogOpen: false,
  },
};

export const getters = {
  // Todos, arquivados inclusos — período antigo do relatório precisa do nome.
  getReasons: _state => _state.reasons,
  getActiveReasons: _state => _state.reasons.filter(reason => reason.active),
  getCurrentPause: _state => _state.current,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  fetchReasons: async ({ commit }) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await PauseReasonsAPI.get();
      commit(types.SET_PAUSE_REASONS, data.payload);
    } finally {
      commit(types.SET_PAUSE_REASONS_UI_FLAG, { isFetching: false });
    }
  },

  createReason: async ({ commit }, payload) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isSaving: true });
    try {
      const { data } = await PauseReasonsAPI.create({ pause_reason: payload });
      commit(types.UPSERT_PAUSE_REASON, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_PAUSE_REASONS_UI_FLAG, { isSaving: false });
    }
  },

  updateReason: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isSaving: true });
    try {
      const { data } = await PauseReasonsAPI.update(id, {
        pause_reason: payload,
      });
      commit(types.UPSERT_PAUSE_REASON, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_PAUSE_REASONS_UI_FLAG, { isSaving: false });
    }
  },

  // Pausa aberta no servidor. Logo depois de pausar o período pode ainda não
  // existir (nasce num job): aí mantém o que já está na tela.
  syncCurrentPause: async ({ commit }) => {
    const { data } = await MyPauseAPI.get();
    if (!data.paused) return;
    commit(types.SET_CURRENT_PAUSE, {
      reason: data.reason || '',
      startedAt: new Date(data.started_at).getTime(),
    });
  },

  openReasonDialog: ({ commit }) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isReasonDialogOpen: true });
  },

  closeReasonDialog: ({ commit }) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isReasonDialogOpen: false });
  },

  clearCurrentPause: ({ commit }) => {
    commit(types.SET_CURRENT_PAUSE, { ...EMPTY_PAUSE });
  },

  // Também troca de motivo com o agente já pausado: o servidor fecha o
  // período anterior e abre outro.
  pause: async ({ commit, state: $state, rootGetters, dispatch }, reason) => {
    const previous = $state.current;
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isChangingPause: true });
    commit(types.SET_CURRENT_PAUSE, { reason, startedAt: Date.now() });
    try {
      await dispatch(
        'updateAvailability',
        {
          availability: 'busy',
          account_id: rootGetters.getCurrentAccountId,
          pause_reason: reason,
        },
        { root: true }
      );
    } catch (error) {
      commit(types.SET_CURRENT_PAUSE, previous);
      throw error;
    } finally {
      commit(types.SET_PAUSE_REASONS_UI_FLAG, { isChangingPause: false });
    }
  },

  resume: async ({ commit, rootGetters, dispatch }) => {
    commit(types.SET_PAUSE_REASONS_UI_FLAG, { isChangingPause: true });
    try {
      await dispatch(
        'updateAvailability',
        { availability: 'online', account_id: rootGetters.getCurrentAccountId },
        { root: true }
      );
      commit(types.SET_CURRENT_PAUSE, { ...EMPTY_PAUSE });
    } finally {
      commit(types.SET_PAUSE_REASONS_UI_FLAG, { isChangingPause: false });
    }
  },
};

export const mutations = {
  [types.SET_PAUSE_REASONS_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [types.SET_PAUSE_REASONS](_state, reasons) {
    _state.reasons = [...reasons].sort(byName);
  },
  [types.UPSERT_PAUSE_REASON](_state, reason) {
    const others = _state.reasons.filter(item => item.id !== reason.id);
    _state.reasons = [...others, reason].sort(byName);
  },
  [types.SET_CURRENT_PAUSE](_state, pause) {
    _state.current = { ...pause };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
